// Phase C.6 — ATHLETE IDENTITY & ROSTER HISTORY FOUNDATION. Pure
// grouping/formatting helpers over an athlete's team_athletes rows (as
// returned by rosterService.listAthleteTeamMemberships(), including its
// nested team -> division -> season embed) for the "Football History"
// read view (FootballHistorySection.js). No fetch, no React — directly
// unit-testable with realistic row shapes.
//
// GRADE VS DIVISION — season_grade (047) is the athlete's real seasonal
// school grade for THAT membership; it is never derived from a
// Division's own grade_min/grade_max here or anywhere else. Division
// context (divisionLabel below) is surfaced separately, only to help
// disambiguate the Team when useful — the two are never conflated into
// one label.
//
// SEASON AS THE PRIMARY CHRONOLOGICAL GROUPING — prefers the real
// Season a Team's Division belongs to (team.division.season.year);
// falls back to the Team's own legacy `season` integer column for a
// Team never assigned to a Division (pre-036 data, or a Team still in
// Setup). Never fabricates a year when neither is available.

import { formatPositions } from './footballPositions';

/** The chronological year for one membership row, or null when neither
 * a real Season nor a legacy team.season value is available. */
export function effectiveSeasonYear(membership) {
  const year = membership?.team?.division?.season?.year;
  if (typeof year === 'number') return year;
  const legacy = membership?.team?.season;
  return typeof legacy === 'number' ? legacy : null;
}

/** Groups memberships by effectiveSeasonYear(), most recent season
 * first; a membership with no derivable year sorts last, under its own
 * "unknown" group, rather than being silently dropped. Each group keeps
 * every membership for that year (almost always one, but never assumed
 * to be exactly one — an athlete could in principle be on two Teams in
 * the same season). */
export function groupMembershipsBySeason(memberships) {
  const byYear = new Map();
  for (const m of memberships || []) {
    const year = effectiveSeasonYear(m);
    const key = year === null ? 'unknown' : year;
    if (!byYear.has(key)) byYear.set(key, { year, memberships: [] });
    byYear.get(key).memberships.push(m);
  }
  return [...byYear.values()].sort((a, b) => {
    if (a.year === null) return 1;
    if (b.year === null) return -1;
    return b.year - a.year;
  });
}

const ORDINAL_ONES = { 1: 'st', 2: 'nd', 3: 'rd' };

function ordinalSuffix(n) {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return 'th';
  return ORDINAL_ONES[n % 10] || 'th';
}

/** "8th Grade" / "Kindergarten" / null (never a bare "Unknown" or a
 * fabricated value) — null for a legacy row whose season_grade was
 * honestly left unset by the 047 backfill (never substituted with the
 * athlete's current grade, which would misrepresent history). */
export function gradeLabel(seasonGrade) {
  if (!seasonGrade || seasonGrade === 'Unknown') return null;
  if (seasonGrade === 'K') return 'Kindergarten';
  const n = Number(seasonGrade);
  if (!Number.isFinite(n)) return null;
  return `${n}${ordinalSuffix(n)} Grade`;
}

/** The Division name for one membership, or null when the Team has none
 * (never inferred, never fabricated) — a separate, optional
 * disambiguation line, never merged into the grade/season display. */
export function divisionLabel(membership) {
  return membership?.team?.division?.name || null;
}

/** "8th Grade · #7 · LB" — the one-line seasonal roster context for a
 * membership. A missing grade/jersey number reads honestly as "Grade —"
 * / "#—", never silently omitted or backfilled from a live/current
 * value. */
export function formatRosterLine(membership) {
  const grade = gradeLabel(membership?.season_grade);
  const jersey = membership?.jersey_number === null || membership?.jersey_number === undefined
    ? '#—'
    : `#${membership.jersey_number}`;
  return [grade || 'Grade —', jersey, formatPositions(membership)].join(' · ');
}

/** Phase C.8 — ATHLETE CARD FOUNDATION. The membership the Athlete Card
 * should treat as "current" — the first ACTIVE row in a memberships
 * array already ordered the way listAthleteTeamMemberships() itself
 * returns it (status asc, added_at desc, 012) — never a second,
 * independent sort. Returns null when the athlete has no active
 * membership at all (never fabricated); the Card's own identity section
 * is expected to render an honest "not currently rostered" state in
 * that case, exactly like every other "no data yet" case in this app.
 *
 * An athlete legitimately rostered on more than one Team at once (C.6
 * already allows this) simply has the FIRST such active row picked —
 * this phase's Card shows one current context, not simultaneous
 * multi-Team identity; that's a deliberate, reported scope choice, not
 * an oversight. */
export function resolveCurrentMembership(memberships) {
  return (memberships || []).find(m => m.status === 'active') || null;
}
